import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, MedicalTreatment } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { syncSingleContactConnection } from "@/lib/utils/connections/item-contact-connections";
import {
  getMedicalTreatmentContactConnection,
  MEDICAL_TREATMENT_CONTACT_CONNECTION_TYPES,
  type SupportedMedicalTreatmentContactRole,
} from "@/lib/utils/connections/medical-treatment-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

type MedicalTreatmentContactIdsByRole = Partial<
  Record<SupportedMedicalTreatmentContactRole, string | undefined>
>;

export const useSyncMedicalTreatmentContactConnections = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matterId,
      currentTreatment,
      desiredContactIdsByRole,
      treatmentId,
    }: {
      currentTreatment?: MedicalTreatment;
      desiredContactIdsByRole: MedicalTreatmentContactIdsByRole;
      matterId: string;
      treatmentId: string;
    }) => {
      await Promise.all(
        Object.entries(desiredContactIdsByRole).map(([role, contactId]) =>
          syncSingleContactConnection({
            matterId,
            currentConnections: currentTreatment?.connections,
            connectionType:
              MEDICAL_TREATMENT_CONTACT_CONNECTION_TYPES[
                role as SupportedMedicalTreatmentContactRole
              ],
            current: getMedicalTreatmentContactConnection(
              currentTreatment,
              role as SupportedMedicalTreatmentContactRole,
            ),
            desiredContactId: contactId,
            sourceObjectId: treatmentId,
            sourceObjectType: "matter_medical_treatment",
          }),
        ),
      );
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error updating medical treatment connections",
      );
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
  });
};
